import React, {useEffect, useState} from "react";
import {Button, OverlayTrigger, Tooltip} from "react-bootstrap";
import ClinicOverviewForm from "./ClinicOverviewForm";
import clinicService from "../../../../../services/clinic.service";
import {useParams} from "react-router-dom";
import Loading from "../../../../common/Loading";
import Swal from '../../../../helpers/swal';

const ActionTooltipButton = ({tooltip, tooltipId, children, ...buttonProps}) => (
    <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}>
        <Button {...buttonProps}>{children}</Button>
    </OverlayTrigger>
);

const InfoField = ({label, icon, iconTone = 'default', children}) => (
    <div className="clinic-overview-field">
        <div className="clinic-overview-field__label">
            <span className={`clinic-overview-field__icon clinic-overview-field__icon--${iconTone}`}>
                <i className={`bi ${icon}`}></i>
            </span>
            <span className="notifications-info-label">{label}</span>
        </div>
        <div className="notifications-info-value clinic-overview-field__value">{children ?? '-'}</div>
    </div>
);

const ClinicOverview = ({updateLogo}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [clinic, setClinic] = useState({});
    const {clinicId} = useParams();
    const [isStatusLoading, setIsStatusLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        getClinicData();
    }, [clinicId, updateLogo]);

    const getClinicData = () => {
        setIsLoading(true);
        clinicService
            .getClinicById(clinicId)
            .then((clinic) => {
                setClinic(clinic.data);
                updateLogo(clinic.data.logo);
                setIsLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setIsLoading(false);
            });
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(clinicId);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleToggleStatus = () => {
        const action = clinic.status === 'ON' ? 'OFF' : 'ON';
        const iconClass = clinic.status === 'ON' ? 'bi-power-off' : 'bi-power';
        Swal.fire({
            title: 'Are you sure?',
            html: `You're about to turn <strong>${action}</strong> the ${clinic.name}`,
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: `
                <i class="bi bi-x"></i>
                Close
            `,
            confirmButtonText: `
            <i class="bi ${iconClass} me-1"></i>
            Yes, turn ${clinic.status === 'ON' ? 'OFF' : 'ON'}
            `,
            buttonsStyling: false,
            customClass: {
                confirmButton: `btn ${clinic.status === 'ON' ? 'btn-danger' : 'btn-success'} me-2 btn-sm`,
                cancelButton: 'btn btn-secondary btn-sm',
                title: `card-title`,
                html: `small`
            }
        }).then((result) => {
            try {
                if (result.isConfirmed) {
                    setIsStatusLoading(true);
                    const newStatus = clinic.status === 'ON' ? 'OFF' : 'ON';

                    clinicService.patchClinic(clinic.id, newStatus)
                        .then(() => {
                            setClinic(prev => ({...prev, status: newStatus}));
                            getClinicData();
                        })
                        .catch((err) => {
                            console.log(err);
                        });
                }
            } catch (error) {
            } finally {
                setIsStatusLoading(false);
            }
        });
    };

    const handleSaved = () => {
        setIsEditing(false);
        getClinicData();
    };

    const isActive = clinic.status === 'ON';

    return (
        <div className="clinic-overview-panel">
            <div className="d-flex align-items-center justify-content-between gap-2 mb-3 flex-wrap">
                <div>
                    <h5 className="clinic-overview__title mb-1">
                        <i className="bi bi-hospital text-primary me-1"></i>
                        Clinic Overview
                    </h5>
                    <p className="text-muted small mb-0">
                        View and manage core clinic details, contact information, and account status.
                    </p>
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="badge bg-light text-dark border font-monospace small">ID: {clinicId}</span>
                    <ActionTooltipButton
                        tooltip={copied ? 'Copied!' : 'Copy clinic ID'}
                        tooltipId="copy-clinic-overview-id"
                        variant="outline-secondary"
                        size="sm"
                        onClick={copyToClipboard}
                    >
                        <i className={`bi ${copied ? 'bi-check2 text-success' : 'bi-copy'}`}></i>
                    </ActionTooltipButton>
                </div>
            </div>

            <div className="notifications-panel">
                <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                    <span className="d-flex align-items-center gap-2">
                        <i className="bi bi-info-circle"></i>
                        {isEditing ? 'Edit Clinic Details' : 'Clinic Details'}
                    </span>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        {isEditing ? (
                            <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => setIsEditing(false)}
                            >
                                <i className="bi bi-x-lg me-1"></i>
                                Cancel
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => setIsEditing(true)}
                            >
                                <i className="bi bi-pencil me-1"></i>
                                Edit
                            </button>
                        )}
                        <button
                            type="button"
                            className={`btn btn-sm ${isActive ? 'btn-danger' : 'btn-success'}`}
                            onClick={handleToggleStatus}
                            disabled={isStatusLoading || isLoading}
                            title="Turn clinic ON or OFF"
                        >
                            {isStatusLoading ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            ) : (
                                <i className={`bi ${isActive ? 'bi-power' : 'bi-power'} me-1`}></i>
                            )}
                            {isActive ? 'Turn Off' : 'Turn On'}
                        </button>
                    </div>
                </div>

                <div className="notifications-panel__body">
                    {isLoading ? (
                        <Loading/>
                    ) : isEditing ? (
                        <ClinicOverviewForm
                            clinicinfo={clinic}
                            onSaved={handleSaved}
                            onCancel={() => setIsEditing(false)}
                        />
                    ) : (
                        <>
                            <div className="clinic-overview-toolbar">
                                <div className="d-flex flex-wrap gap-2">
                                    <span className={`clinic-module-stat--compact ${isActive ? 'clinic-module-stat--success' : 'clinic-module-stat--danger'}`}>
                                        <i className={`bi ${isActive ? 'bi-check-circle' : 'bi-x-circle'} me-1`}></i>
                                        {clinic.status || 'Unknown'}
                                    </span>
                                    {clinic.clinicType && (
                                        <span className="clinic-overview-type-badge">{clinic.clinicType}</span>
                                    )}
                                    {clinic.number && (
                                        <span className="clinic-module-stat--compact clinic-module-stat--total">
                                            #{clinic.number}
                                        </span>
                                    )}
                                </div>
                                {clinic.modifiedAt && (
                                    <span className="clinic-overview-meta">
                                        <i className="bi bi-clock-history"></i>
                                        Last modified: {clinic.modifiedAt}
                                    </span>
                                )}
                            </div>

                            <div className="clinic-info-section-title clinic-info-section-title--first">
                                Basic Information
                            </div>
                            <div className="clinic-overview-grid">
                                <InfoField label="Name" icon="bi-building" iconTone="name">{clinic.name}</InfoField>
                                <InfoField label="Type" icon="bi-tag" iconTone="type">{clinic.clinicType}</InfoField>
                                <InfoField label="Start Date" icon="bi-calendar-event" iconTone="date">{clinic.dateStarted}</InfoField>
                                <InfoField label="Clinic Number" icon="bi-hash" iconTone="number">{clinic.number ? `#${clinic.number}` : null}</InfoField>
                            </div>

                            <div className="clinic-info-section-title">Contact Information</div>
                            <div className="clinic-overview-grid">
                                <InfoField label="Email" icon="bi-envelope" iconTone="email">
                                    {clinic.email ? (
                                        <a href={`mailto:${clinic.email}`} className="clinic-overview-link">
                                            {clinic.email}
                                        </a>
                                    ) : null}
                                </InfoField>
                                <InfoField label="Phone" icon="bi-telephone" iconTone="phone">{clinic.phone}</InfoField>
                                <InfoField label="SMS" icon="bi-chat-dots" iconTone="sms">{clinic.sms}</InfoField>
                                <InfoField label="Website" icon="bi-globe" iconTone="website">
                                    {clinic.website ? (
                                        <a
                                            href={clinic.website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="clinic-overview-link"
                                        >
                                            {clinic.website}
                                            <i className="bi bi-box-arrow-up-right ms-1 small"></i>
                                        </a>
                                    ) : null}
                                </InfoField>
                            </div>

                            <div className="clinic-info-section-title">Additional Information</div>
                            <div className="clinic-overview-grid">
                                <InfoField label="Signature" icon="bi-pen" iconTone="signature">{clinic.signature}</InfoField>
                                <InfoField label="Logo" icon="bi-image" iconTone="logo">
                                    {clinic.logo ? (
                                        <a
                                            href={clinic.logo}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="clinic-overview-link"
                                        >
                                            View logo
                                            <i className="bi bi-box-arrow-up-right ms-1 small"></i>
                                        </a>
                                    ) : null}
                                </InfoField>
                                <InfoField label="Created" icon="bi-calendar-plus" iconTone="created">{clinic.createdAt}</InfoField>
                                <InfoField label="Modified" icon="bi-clock-history" iconTone="modified">{clinic.modifiedAt}</InfoField>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClinicOverview;
